import React, { useState } from "react";
import { FileDown, Filter, Loader2, X, Eye, Download, ArrowLeft, Printer } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { API_URL, getToken } from "../lib/utils";
import { toast } from "./ToastProvider";

export const RelatorioFilterModal = ({
  open,
  onOpenChange,
  title,
  description,
  endpoint,
  filters = [],
  extraParams = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("relatorio.pdf");

  const handleFilterChange = (key, value) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value === "todos" ? "" : value
    }));
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value && value !== "todos") {
          params.append(key, value);
        }
      });
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const queryString = params.toString();
      const url = `${API_URL}/api${endpoint}${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, {
        headers: { "Authorization": `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error("Erro ao gerar relatório");

      const blob = await response.blob();
      setPdfBlob(blob);

      const contentDisposition = response.headers.get("content-disposition");
      let filename = "relatorio.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      setPdfFilename(filename);

      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } catch (error) {
      console.error("Erro:", error);
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBlob) return;
    const downloadUrl = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = pdfFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    toast.success("PDF baixado com sucesso!");
  };

  const handlePrint = () => {
    if (!previewUrl) return;
    const printWindow = window.open(previewUrl, "_blank");
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        printWindow.print();
      });
    }
  };

  const handleBackToFilters = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPdfBlob(null);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setPdfBlob(null);
      setFilterValues({});
    }
    onOpenChange(isOpen);
  };

  const handleClearFilters = () => {
    setFilterValues({});
  };

  // Preview mode
  if (previewUrl) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0" data-testid="pdf-preview-modal">
          <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={handleBackToFilters} data-testid="preview-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
              <div className="h-5 w-px bg-slate-300" />
              <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="preview-print-btn">
                <Printer className="w-4 h-4 mr-1" />
                Imprimir
              </Button>
              <Button size="sm" onClick={handleDownload} className="bg-teal-600 hover:bg-teal-700" data-testid="preview-download-btn">
                <Download className="w-4 h-4 mr-1" />
                Baixar PDF
              </Button>
            </div>
          </div>
          <div className="flex-1 bg-slate-200 overflow-auto flex justify-center p-4">
            <iframe
              src={previewUrl}
              className="w-full max-w-[210mm] bg-white shadow-xl rounded"
              style={{ minHeight: "100%", border: "none" }}
              title="Pré-visualização do PDF"
              data-testid="pdf-preview-iframe"
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Filter mode
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-teal-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {filters.map((filter) => (
            <div key={filter.key} className="space-y-2">
              <Label>{filter.label}</Label>

              {filter.type === "select" && (
                <Select
                  value={filterValues[filter.key] || "todos"}
                  onValueChange={(value) => handleFilterChange(filter.key, value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={filter.placeholder || "Selecione..."} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {filter.options?.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {filter.type === "date" && (
                <Input
                  type="date"
                  value={filterValues[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                />
              )}

              {filter.type === "text" && (
                <Input
                  type="text"
                  placeholder={filter.placeholder}
                  value={filterValues[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                />
              )}
            </div>
          ))}

          {filters.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" />
              Limpar filtros
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handlePreview}
            disabled={loading}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="preview-generate-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Visualizar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RelatorioFilterModal;
